import React, { useEffect, useMemo, useState } from "react";
import Chart from "react-apexcharts";
import { useReport } from "../../DataStore/ReportContext";
import { Skeleton } from "../../util/skeleton/Skeleton";

const weekLabels = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const fmt = (n) =>
  Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });

/* Theme */
const THEME = {
  indigo: "#4F46E5",
  indigoMuted: "rgba(79,70,229,.35)",
  slateText: "rgba(71,85,105,.9)",
  slateMuted: "rgba(71,85,105,.75)",
  border: "rgba(15,23,42,.06)",
  grid: "rgba(148,163,184,.28)",
  headBg: "linear-gradient(180deg, rgba(79,70,229,.06), rgba(255,255,255,0))",
};

const ExpenseGraphs = () => {
  const [activeGraph, setActiveGraph] = useState("Weekly");

  const {
    weeklyIncomeGraph,
    monthlyIncomeGraph,
    loadingWeeklyIncomeGraph,
    loadingMonthlyIncomeGraph,
  } = useReport();
  /* ---------------- data ---------------- */
  const weeklySeries = useMemo(
    () =>
      Array.isArray(weeklyIncomeGraph) && weeklyIncomeGraph.length
        ? weeklyIncomeGraph
        : [
            { name: "Income", data: [0, 0, 0, 0, 0, 0, 0] },
            { name: "Expenditure", data: [0, 0, 0, 0, 0, 0, 0] },
          ],
    [weeklyIncomeGraph]
  );

  const monthlySeries = useMemo(
    () =>
      monthlyIncomeGraph?.data?.length
        ? monthlyIncomeGraph.data
        : [
            { name: "Income", data: new Array(12).fill(0) },
            { name: "Expenditure", data: new Array(12).fill(0) },
          ],
    [monthlyIncomeGraph]
  );

  const monthlyCategories = useMemo(
    () => monthlyIncomeGraph?.months || [],
    [monthlyIncomeGraph]
  );

  /* ---------------- base chart options (theme) ---------------- */
  const baseOptions = useMemo(
    () => ({
      chart: {
        toolbar: { show: false },
        zoom: { enabled: false },
        fontFamily: "inherit",
        foreColor: THEME.slateMuted,
      },
      grid: {
        borderColor: THEME.grid,
        strokeDashArray: 4,
        padding: { left: 6, right: 6 },
      },
      dataLabels: { enabled: false },
      legend: {
        position: "top",
        horizontalAlign: "left",
        fontWeight: 700,
        labels: { colors: THEME.slateText },
        markers: { radius: 10 },
      },
      colors: [THEME.indigo, THEME.indigoMuted],
      tooltip: {
        theme: "light",
        y: { formatter: fmt },
      },
      xaxis: {
        axisBorder: { show: false },
        axisTicks: { show: false },
        labels: { style: { colors: THEME.slateMuted } },
      },
      yaxis: {
        labels: {
          formatter: fmt,
          style: { colors: THEME.slateMuted },
        },
      },
    }),
    []
  );

  /* ---------------- weekly options ---------------- */
  const weeklyOptions = useMemo(
    () => ({
      ...baseOptions,
      chart: { ...baseOptions.chart, type: "area" },
      xaxis: { ...baseOptions.xaxis, categories: weekLabels },
      stroke: { curve: "smooth", width: 2 },
      fill: {
        type: "gradient",
        gradient: { shadeIntensity: 0.2, opacityFrom: 0.18, opacityTo: 0.02 },
      },
    }),
    [baseOptions]
  );

  /* ---------------- monthly options ---------------- */
  const monthlyOptions = useMemo(
    () => ({
      ...baseOptions,
      chart: { ...baseOptions.chart, type: "bar" },
      xaxis: { ...baseOptions.xaxis, categories: monthlyCategories },
      plotOptions: {
        bar: {
          columnWidth: "52%",
          borderRadius: 8,
          borderRadiusApplication: "end",
        },
      },
      stroke: { show: true, width: 2, colors: ["transparent"] },
    }),
    [baseOptions, monthlyCategories]
  );

  /* ---------------- header ---------------- */
  const SectionHeader = () => (
    <div className="yx-graphHead">
      <div style={{ minWidth: 0 }}>
        <div className="yx-graphKicker">Cashflow</div>
        <div className="yx-graphTitle">Income vs Expenditure</div>
        <div className="yx-graphSub">Weekly and annual trend</div>
      </div>

      <div className="yx-graphToggle">
        <button
          type="button"
          className={`yx-toggleBtn ${activeGraph === "Weekly" ? "is-active" : ""}`}
          onClick={() => setActiveGraph("Weekly")}
        >
          This week
        </button>
        <button
          type="button"
          className={`yx-toggleBtn ${activeGraph === "Monthly" ? "is-active" : ""}`}
          onClick={() => setActiveGraph("Monthly")}
        >
          This year
        </button>
      </div>
    </div>
  );

  const GraphSkeleton = () => (
    <div>
      <div className="d-flex align-items-center mb-2" style={{ gap: 10 }}>
        <Skeleton w="90px" h={12} />
        <Skeleton w="120px" h={12} />
      </div>
      <Skeleton className="sk-graph" style={{ display: "block", height: 220, borderRadius: 14 }} />
    </div>
  );

  const showWeeklyLoading = loadingWeeklyIncomeGraph && !weeklyIncomeGraph?.length;
  const showMonthlyLoading = loadingMonthlyIncomeGraph && !monthlyIncomeGraph?.data?.length;

  return (
    <>
      <style>{styles}</style>

      <div className="card yx-graphCard mb-3">
        <SectionHeader />

        <div className="card-body yx-graphBody">
          {activeGraph === "Weekly" &&
            (showWeeklyLoading ? (
              <GraphSkeleton />
            ) : (
              <Chart options={weeklyOptions} series={weeklySeries} type="area" height={220} />
            ))}

          {activeGraph === "Monthly" &&
            (showMonthlyLoading ? (
              <GraphSkeleton />
            ) : (
              <Chart options={monthlyOptions} series={monthlySeries} type="bar" height={220} />
            ))}
        </div>
      </div>
    </>
  );
};

const styles = `
  .yx-graphCard{
    border-radius: 18px;
    border: 1px solid ${THEME.border};
    background: #fff;
    box-shadow: 0 1px 2px rgba(16,24,40,.04);
    overflow: hidden;
  }

  .yx-graphHead{
    padding: 14px 16px 12px;
    border-bottom: 1px solid ${THEME.border};
    background: ${THEME.headBg};
    display:flex;
    align-items:flex-start;
    justify-content: space-between;
    gap: 14px;
  }

  .yx-graphKicker{
    font-size: 11px;
    font-weight: 900;
    letter-spacing: .10em;
    text-transform: uppercase;
    color: ${THEME.slateMuted};
  }

  .yx-graphTitle{
    margin-top: 6px;
    font-size: 16px;
    font-weight: 950;
    
    color: rgba(15,23,42,.92);
  }

  .yx-graphSub{
    margin-top: 3px;
    font-size: 12px;
    font-weight: 800;
    color: ${THEME.slateText};
  }

  .yx-graphToggle{
    display:flex;
    align-items:center;
    gap: 8px;
    flex: 0 0 auto;
  }

  .yx-toggleBtn{
    border: 1px solid rgba(79,70,229,.14);
    background: rgba(79,70,229,.06);
    color: rgba(71,85,105,.90);
    font-weight: 600;
    font-size: 12.5px;
    border-radius: 14px;
    padding: 5px 12px;
    transition: background .15s ease, border-color .15s ease, color .15s ease;
  }

  .yx-toggleBtn:hover{
    background: rgba(79,70,229,.10);
  }

  .yx-toggleBtn.is-active{
    background: rgba(79,70,229,.14);
    border-color: rgba(79,70,229,.20);
    color: ${THEME.indigo};
  }

  .yx-graphBody{
    padding: 14px 14px 10px;
  }

  /* Apex tweaks */
  .yx-graphCard .apexcharts-legend{
    padding: 0 !important;
  }

  @media (max-width: 576px){
    .yx-graphHead{ flex-direction: column; align-items: stretch; }
    .yx-graphToggle{ justify-content: flex-start; }
  }
`;

export default ExpenseGraphs;
